import React, { useEffect, useState } from 'react';
import {
  AdminButton,
  AdminCard,
  AdminInput,
  AdminPageShell,
  AdminSelect,
  AdminStatusPill,
  AdminTable
} from '../AdminUI';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const emptyForm = {
  name: '',
  discount_type: 'percentage',
  discount_value: 10,
  starts_at: '',
  ends_at: '',
  is_active: true,
  notes: ''
};

const SaleCampaignsPage = () => {
  const token = localStorage.getItem('adminToken');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);

  const fetchItems = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/admin/sale-campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load campaigns');
      setItems(data.campaigns || []);
    } catch (err) {
      setError(err.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const createCampaign = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        discount_value: Number(form.discount_value),
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null
      };
      const res = await fetch(`${API_BASE_URL}/admin/sale-campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to create campaign');
      setItems((prev) => [data.campaign, ...prev]);
      setForm(emptyForm);
    } catch (err) {
      setError(err.message || 'Failed to create campaign');
    } finally {
      setSaving(false);
    }
  };

  const pauseResume = async (item) => {
    setError('');
    try {
      const endpoint = item.is_active ? 'pause' : 'resume';
      const res = await fetch(`${API_BASE_URL}/admin/sale-campaigns/${item.id}/${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to update campaign');
      setItems((prev) => prev.map((c) => (c.id === item.id ? { ...c, ...data.campaign } : c)));
    } catch (err) {
      setError(err.message || 'Failed to update campaign');
    }
  };

  const deleteCampaign = async (item) => {
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/admin/sale-campaigns/${item.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to delete campaign');
      setItems((prev) => prev.filter((c) => c.id !== item.id));
    } catch (err) {
      setError(err.message || 'Failed to delete campaign');
    }
  };

  return (
    <AdminPageShell
      title="Sale Campaigns"
      description="Configure global discounts that apply automatically when no promo code is used."
    >
      {error ? <AdminCard className="border-rose-200 text-rose-600 dark:border-rose-900/40">{error}</AdminCard> : null}

      <AdminCard>
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">Create Campaign</h2>
        <form onSubmit={createCampaign} className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <AdminInput placeholder="Campaign name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
          <AdminSelect value={form.discount_type} onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))}>
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed (cents)</option>
          </AdminSelect>
          <AdminInput
            type="number"
            min={1}
            placeholder={form.discount_type === 'percentage' ? 'Discount %' : 'Discount cents'}
            value={form.discount_value}
            onChange={(e) => setForm((f) => ({ ...f, discount_value: e.target.value }))}
            required
          />
          <AdminInput type="datetime-local" value={form.starts_at} onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))} />
          <AdminInput type="datetime-local" value={form.ends_at} onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))} />
          <AdminInput placeholder="Notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 dark:text-slate-300">Status</span>
            <AdminSelect value={form.is_active ? 'active' : 'paused'} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.value === 'active' }))}>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </AdminSelect>
          </div>
          <div className="md:col-span-2 md:text-right">
            <AdminButton type="submit" disabled={saving}>
              {saving ? 'Creating...' : 'Create campaign'}
            </AdminButton>
          </div>
        </form>
      </AdminCard>

      <AdminCard className="p-0">
        {loading ? (
          <div className="p-4 text-slate-500">Loading...</div>
        ) : (
          <AdminTable
            columns={[
              { key: 'name', label: 'Name' },
              { key: 'discount', label: 'Discount', render: (row) => (row.discount_type === 'percentage' ? `${row.discount_value}%` : `${row.discount_value} cents`) },
              { key: 'status', label: 'Status', render: (row) => <AdminStatusPill value={row.status || (row.is_active ? 'active' : 'paused')} /> },
              { key: 'starts_at', label: 'Starts', render: (row) => (row.starts_at ? new Date(row.starts_at).toLocaleString() : '-') },
              { key: 'ends_at', label: 'Ends', render: (row) => (row.ends_at ? new Date(row.ends_at).toLocaleString() : '-') },
              { key: 'payments_count', label: 'Payments', render: (row) => row.payments_count || 0 },
              { key: 'savings_total_cents', label: 'Savings', render: (row) => row.savings_total_cents || 0 },
              {
                key: 'actions',
                label: 'Actions',
                render: (row) => (
                  <div className="flex flex-wrap gap-2">
                    <AdminButton variant="secondary" className="px-2.5 py-1.5 text-xs" onClick={() => pauseResume(row)}>
                      {row.is_active ? 'Pause' : 'Resume'}
                    </AdminButton>
                    <AdminButton variant="danger" className="px-2.5 py-1.5 text-xs" onClick={() => deleteCampaign(row)}>
                      Delete
                    </AdminButton>
                  </div>
                )
              }
            ]}
            rows={items}
          />
        )}
      </AdminCard>
    </AdminPageShell>
  );
};

export default SaleCampaignsPage;
